// src/components/ExamScratchPadPanel.jsx
// The exam-wide Scratch Pad - a plain-text notes area available from every
// question (see the "Scratch Pad" button in ExamSplitScreen's header row,
// matching the real ACCA CBE toolbar's placement), NOT per-question like the
// Word Processor/Spreadsheet responses. One value lives on the session
// document itself (`scratchPad`), so closing and reopening the panel, or
// moving to a different question, always shows the same content.
//
// Deliberately never read by generateExamPdf.js or functions/index.js's
// abandoned-session recovery renderer - both only ever look at
// `session.answers`, so this can never end up in what a marker sees unless
// the student themselves copies it into an actual answer.
//
// No custom Cut/Copy/Paste/Undo/Redo buttons: a plain <textarea> already
// gives students all of that for free via the keyboard and right-click menu
// in every browser (including Safari), whereas a synthetic
// document.execCommand('paste') button does not - most browsers block
// script-triggered clipboard reads outright.

import React, { useRef, useCallback, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useWriteGuard } from '../utils/writeGuard';

const AUTOSAVE_DEBOUNCE_MS = 30000;
const AUTOSAVE_MAX_WAIT_MS = 30000;

const ExamScratchPadPanel = ({ sessionId, initialValue, initialSavedAt }) => {
  // Seeded from the last save already sitting in Firestore, not just null -
  // otherwise closing and reopening this panel would forget the content was
  // already saved and show "Not yet saved" again until the next autosave,
  // even though nothing has changed (same fix as ExamAnswerPanel).
  const [savedAt, setSavedAt] = React.useState(() => initialSavedAt?.toDate ? initialSavedAt.toDate() : null);
  const debounceTimerRef = useRef(null);
  const maxWaitTimerRef = useRef(null);
  const latestValueRef = useRef(initialValue || '');
  const { guard, blocked: writeBlocked } = useWriteGuard();

  const flush = useCallback(() => {
    if (debounceTimerRef.current) { clearTimeout(debounceTimerRef.current); debounceTimerRef.current = null; }
    if (maxWaitTimerRef.current) { clearTimeout(maxWaitTimerRef.current); maxWaitTimerRef.current = null; }
    if (!sessionId) return;
    const value = latestValueRef.current;

    guard(async () => {
      try {
        await updateDoc(doc(db, 'examSessions', sessionId), {
          scratchPad: value,
          scratchPadUpdatedAt: serverTimestamp(),
          // Refreshes the session-level idle clock sweepAbandonedExamSessions
          // uses for unlimited-time sessions (functions/index.js) - see
          // ExamAnswerPanel's identical field for the full reasoning.
          lastAutosaveAt: serverTimestamp()
        });
        setSavedAt(new Date());
      } catch (err) {
        console.error('Scratch pad autosave failed:', err);
      }
    });
  }, [sessionId, guard]);

  const handleChange = (e) => {
    latestValueRef.current = e.target.value;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(flush, AUTOSAVE_DEBOUNCE_MS);

    if (!maxWaitTimerRef.current) {
      maxWaitTimerRef.current = setTimeout(flush, AUTOSAVE_MAX_WAIT_MS);
    }
  };

  useEffect(() => () => {
    if (debounceTimerRef.current || maxWaitTimerRef.current) flush();
  }, [flush]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '6px 12px', fontSize: '11px', color: '#95a5a6', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <span>Not part of your submission - only visible to you</span>
        <span>{savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'Not yet saved'}</span>
      </div>

      {writeBlocked && (
        <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', fontSize: '12px', fontWeight: '600', flexShrink: 0 }}>
          Something went wrong and autosave has stopped here. Close and reopen this panel and check your notes are still there.
        </div>
      )}

      <textarea
        defaultValue={initialValue || ''}
        onChange={handleChange}
        placeholder="Jot down anything here - it's never marked or submitted."
        style={{
          flex: 1, minHeight: 0, resize: 'none', border: 'none', outline: 'none',
          padding: '12px', fontSize: '13px', fontFamily: 'inherit', lineHeight: 1.5
        }}
      />
    </div>
  );
};

export default ExamScratchPadPanel;
